// Contributions page component
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { useGetToken } from '@/lib/hooks/use-auth'
import { useUser, usePermissions } from '@/lib/hooks'
import { useProject } from '@/contexts/project-context'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import { format } from 'date-fns'
import ContributionForm from '@/components/custom-ui/contribution-form'

interface Contribution {
  id: string
  user_id: string | null
  task_id: string | null
  description: string | null
  contribution_date: string
  effort_value: number | null
  capital_value: number | null
  qualitative_value: string | null
  tasks: {
    title: string
    category: string | null
  } | null
  users: {
    first_name: string | null
    last_name: string | null
  } | null
}

export default function ContributionsPage() {
  const getToken = useGetToken()
  const [showAddForm, setShowAddForm] = useState(false)
  const queryClient = useQueryClient()
  const { profile } = useUser()
  const { currentProject } = useProject()
  const permissions = usePermissions()

  const { data: contributions, isLoading } = useQuery<Contribution[]>({
    queryKey: ['contributions', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []
      
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
        .from('contributions')
        .select(`
          *,
          tasks (title, category),
          users (first_name, last_name)
        `)
        .eq('project_id', currentProject.id)
        .order('contribution_date', { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!currentProject
  })

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Contributions</h1>
        {permissions.canCreateContributions && (
          <Button onClick={() => setShowAddForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Log Contribution
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      ) : contributions?.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <p className="text-gray-500">No contributions logged yet. Start by logging your first contribution!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contributions?.map((contribution: Contribution) => (
            <div key={contribution.id} className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      {contribution.tasks?.title}
                    </h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {contribution.tasks?.category}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    by {contribution.users?.first_name} {contribution.users?.last_name}
                  </p>
                  <p className="mt-2 text-sm text-gray-700">{contribution.description}</p>
                  {contribution.qualitative_value && (
                    <p className="mt-2 text-sm text-gray-600 italic">
                      "{contribution.qualitative_value}"
                    </p>
                  )}
                </div>
                <div className="ml-6 text-right">
                  <p className="text-sm text-gray-500">
                    {format(new Date(contribution.contribution_date), 'MMM d, yyyy')}
                  </p>
                  <div className="mt-2 space-y-1">
                    {(contribution.effort_value || 0) > 0 && (
                      <p className="text-sm">
                        <span className="text-gray-500">Effort:</span>{' '}
                        <span className="font-medium">{contribution.effort_value}</span>
                      </p>
                    )}
                    {(contribution.capital_value || 0) > 0 && (
                      <p className="text-sm">
                        <span className="text-gray-500">Capital:</span>{' '}
                        <span className="font-medium">${(contribution.capital_value || 0).toLocaleString()}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contribution Form */}
      <ContributionForm
        open={showAddForm}
        onOpenChange={setShowAddForm}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['contributions'] })
          setShowAddForm(false)
        }}
      />
    </div>
  )
}